import { UserCheck, FileText, Stethoscope, CheckCircle, Pill, Check, Clock } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

const STEPS = [
  { key: "assign", label: "Assign", icon: UserCheck },
  { key: "review", label: "Review", icon: FileText },
  { key: "consultation", label: "Consultation", icon: Stethoscope },
  { key: "approve", label: "Approve", icon: CheckCircle },
  { key: "prescribe", label: "Prescribe", icon: Pill },
] as const;

interface WorkflowProgressBarProps {
  triageLevel: string | null;
  status: string;
  hasNotes: boolean;
  hasCompletedConsultation?: boolean;
  hasScheduledConsultation?: boolean;
  hasPrescription?: boolean;
  scheduledTime?: string;
  reviewedAt?: string;
  createdAt?: string;
}

function getActiveStep(props: WorkflowProgressBarProps): number {
  if (props.hasPrescription) return 5;
  if (props.status === "rejected") return 5;
  if (props.status === "approved") return 4;
  if (props.hasCompletedConsultation) return 3;
  if (props.status === "reviewed") return 2;
  if (props.triageLevel && props.status === "pending") return 1;
  if (!props.triageLevel) return 0;
  return 0;
}

function formatDateTime(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" }) +
    " at " +
    d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true });
}

const WorkflowProgressBar = (props: WorkflowProgressBarProps) => {
  const activeIndex = getActiveStep(props);
  const isRejected = props.status === "rejected";

  const getTooltip = (step: typeof STEPS[number], i: number): string | null => {
    const isComplete = i < activeIndex;
    if (step.key === "consultation") {
      if (props.hasCompletedConsultation && props.scheduledTime) {
        return `Completed · ${formatDateTime(props.scheduledTime)}`;
      }
      if (props.hasScheduledConsultation && props.scheduledTime) {
        return `Scheduled · ${formatDateTime(props.scheduledTime)}`;
      }
    }
    if (step.key === "review" && isComplete && props.reviewedAt) {
      return `Reviewed · ${formatDateTime(props.reviewedAt)}`;
    }
    if (step.key === "assign" && isComplete && props.createdAt) {
      return `Assigned · ${formatDateTime(props.createdAt)}`;
    }
    return null;
  };

  return (
    <TooltipProvider delayDuration={200}>
      <div className="w-full py-2">
        <div className="flex items-center justify-between relative">
          {STEPS.map((step, i) => {
            const isComplete = i < activeIndex;
            const isCurrent = i === activeIndex && !isRejected;
            const isScheduled =
              step.key === "consultation" &&
              !props.hasCompletedConsultation &&
              props.hasScheduledConsultation &&
              i >= activeIndex;
            const StepIcon = step.icon;
            const tooltip = getTooltip(step, i);

            const stepContent = (
              <div key={step.key} className="flex flex-col items-center relative z-10 flex-1">
                {i > 0 && (
                  <div
                    className={`absolute top-4 right-1/2 w-full h-0.5 -z-10 ${
                      isComplete ? "bg-primary" : isScheduled ? "bg-amber-400/50" : "bg-border"
                    }`}
                  />
                )}

                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center border-2 transition-colors ${
                    isComplete
                      ? "bg-primary border-primary text-primary-foreground"
                      : isScheduled
                        ? "border-amber-400 bg-amber-50 text-amber-600 animate-pulse"
                        : isCurrent
                          ? "border-primary bg-primary/10 text-primary"
                          : "border-border bg-card text-muted-foreground"
                  }`}
                >
                  {isComplete ? (
                    <Check size={14} strokeWidth={3} />
                  ) : isScheduled ? (
                    <Clock size={14} />
                  ) : (
                    <StepIcon size={14} />
                  )}
                </div>

                <span
                  className={`text-[10px] mt-1.5 font-medium leading-tight text-center ${
                    isComplete
                      ? "text-primary"
                      : isScheduled
                        ? "text-amber-600 font-semibold"
                        : isCurrent
                          ? "text-foreground font-semibold"
                          : "text-muted-foreground"
                  }`}
                >
                  {isScheduled ? "Scheduled" : step.label}
                </span>
              </div>
            );

            if (tooltip) {
              return (
                <Tooltip key={step.key}>
                  <TooltipTrigger asChild>{stepContent}</TooltipTrigger>
                  <TooltipContent side="bottom" className="text-xs">
                    {tooltip}
                  </TooltipContent>
                </Tooltip>
              );
            }

            return stepContent;
          })}
        </div>
      </div>
    </TooltipProvider>
  );
};

export default WorkflowProgressBar;